
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExerciseType, WorkoutMode } from '../types';
import { EXERCISE_CONFIG } from '../constants';
import { Card } from '../components/Card';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [selectedExercise, setSelectedExercise] = useState<ExerciseType | null>(null);

  const startWorkout = (mode: WorkoutMode, durationSeconds: number = 0) => {
    if (!selectedExercise) return;
    navigate(`/workout/${selectedExercise}?mode=${mode}&duration=${durationSeconds}`);
  };

  return (
    <div className="max-w-md mx-auto py-10 px-4 pb-32 min-h-[80vh] relative">
      
      {!selectedExercise && (
        <>
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-black text-white mb-2 uppercase tracking-tighter">Initialize Session</h1>
            <p className="text-secondary font-mono text-xs uppercase tracking-widest">Select training protocol below</p>
          </div>

          <div className="space-y-4 mb-8">
            {(Object.keys(EXERCISE_CONFIG) as ExerciseType[]).map((type) => (
              <button
                key={type}
                onClick={() => setSelectedExercise(type)}
                className={`w-full text-left transition-all duration-200 outline-none group`}
              >
                <Card 
                  className='border border-white/5 bg-surface/50 hover:border-white/20 hover:bg-surface'
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className='text-lg font-bold uppercase tracking-wide text-slate-400 group-hover:text-white'>
                        {EXERCISE_CONFIG[type].name}
                      </h3>
                      <p className="text-xs text-slate-500 font-mono mt-2">
                        {EXERCISE_CONFIG[type].description}
                      </p>
                    </div>
                    <div className='w-5 h-5 border border-slate-700 flex items-center justify-center group-hover:border-white transition-colors'>
                       <div className="w-2 h-2 bg-transparent group-hover:bg-white" />
                    </div>
                  </div>
                </Card>
              </button>
            ))}
          </div>
        </>
      )}

      {selectedExercise && (
        <div className="absolute inset-0 z-10 bg-dark flex flex-col animate-in fade-in duration-300">
           <div className="mb-8 text-center pt-10">
            <button 
              onClick={() => setSelectedExercise(null)}
              className="text-slate-500 text-xs font-mono uppercase tracking-widest hover:text-white mb-4 flex items-center justify-center gap-2"
            >
              ← Back to Protocols
            </button>
            <h1 className="text-3xl font-black text-white mb-2 uppercase tracking-tighter">
              {EXERCISE_CONFIG[selectedExercise].name}
            </h1>
            <p className="text-secondary font-mono text-xs uppercase tracking-widest">Select Mode</p>
          </div>

          <div className="flex-1 space-y-4 px-2">
            <button 
              onClick={() => startWorkout(WorkoutMode.FREE)}
              className="w-full"
            >
              <Card className="border-white/20 bg-surface hover:bg-white/5 transition-colors text-left">
                 <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/10 rounded-full">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white uppercase">Free Workout</h3>
                      <p className="text-xs text-slate-400 font-mono">No time limit. Just reps.</p>
                    </div>
                 </div>
              </Card>
            </button>

            <div className="flex items-center gap-4 py-4">
              <div className="h-px bg-white/10 flex-1"></div>
              <span className="text-xs text-slate-500 font-mono uppercase">OR Time Attack</span>
              <div className="h-px bg-white/10 flex-1"></div>
            </div>

            <button 
              onClick={() => startWorkout(WorkoutMode.TIME_ATTACK, 120)}
              className="w-full"
            >
              <Card className="border-amber-500/20 bg-amber-900/5 hover:bg-amber-500/10 transition-colors text-left relative overflow-hidden group">
                 <div className="absolute top-0 right-0 p-2 opacity-50">
                    <span className="text-[10px] font-bold text-amber-500 border border-amber-500 px-1">HARDCORE</span>
                 </div>
                 <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-500/10 rounded-full group-hover:bg-amber-500/20">
                      <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white uppercase">2 Min Attack</h3>
                      <p className="text-xs text-slate-400 font-mono">Max reps in 120 seconds.</p>
                    </div>
                 </div>
              </Card>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};